import React, { useEffect, useState, useCallback } from 'react';
import { useTelegram } from '../contexts/TelegramContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface TelegramAutoLoginProps {
  children: React.ReactNode;
}

const TelegramAutoLogin: React.FC<TelegramAutoLoginProps> = ({ children }) => {
  const { user: telegramUser, isInTelegram, initData } = useTelegram();
  const { user: authUser, telegramLogin, loading } = useAuth();
  const navigate = useNavigate();
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [isAutoLogging, setIsAutoLogging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAutoLogin = useCallback(async () => {
    if (!telegramUser || !initData) {
      setError('缺少 Telegram 登录数据（telegramUser 或 initData 为空）');
      return;
    }

    setError(null);
    setIsAutoLogging(true);

    try {
      await telegramLogin(telegramUser, initData);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.message;
      const message = serverMessage || err?.message || '未知错误';
      console.error('Telegram auto-login failed:', status, message, err);
      setError(`${status ? `[${status}] ` : ''}${message}`);
    } finally {
      setIsAutoLogging(false);
    }
  }, [telegramUser, initData, telegramLogin, navigate]);

  useEffect(() => {
    // 只在Telegram环境中且用户未登录时尝试自动登录
    if (
      isInTelegram &&
      telegramUser &&
      !authUser &&
      !loading &&
      !autoLoginAttempted &&
      initData
    ) {
      setAutoLoginAttempted(true);
      runAutoLogin();
    }
  }, [isInTelegram, telegramUser, authUser, loading, autoLoginAttempted, initData, runAutoLogin]);

  // 正在自动登录：显示加载状态
  if (isAutoLogging) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">正在通过 Telegram 登录...</p>
        </div>
      </div>
    );
  }

  // 自动登录失败：在 Telegram 环境内显示错误详情与操作，便于排查（而不是静默卡住）
  if (error && isInTelegram && !authUser) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Telegram 自动登录失败</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 break-words mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={runAutoLogin}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              重试自动登录
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              使用账号密码登录
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default TelegramAutoLogin;
